import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    model,
    OnInit,
    signal,
    untracked,
} from '@angular/core';
import { catchError, EMPTY } from 'rxjs';

/** Component imports */
import { AutocompleteComponent, BaseQueryParams, ClientTypeCode, FilterOperator } from '@shared';

/** Local imports */
import {
    ApiDealerAdvertiser,
    ApiDealerHost,
    ApiDealerInformation,
} from '@core/services/dealer/dealer.interface';
import { DealerService } from '@core/services/dealer/dealer.service';
import {
    UiAdvertisersAutocomplete,
    UiDealersAutocomplete,
    UiHostsAutocomplete,
} from '@features/dealers/interfaces';
import { Logger, searchHandler, withLoading } from '@shared/utils';

@Component({
    selector: 'app-dealer-selection-base',
    standalone: true,
    imports: [CommonModule, AutocompleteComponent],
    templateUrl: './dealer-selection-base.component.html',
    styleUrl: './dealer-selection-base.component.scss',
})
export class DealerSelectionBaseComponent implements OnInit {
    /**
     * Service in managing dealer-related data and API operations
     */
    private dealerService = inject(DealerService);

    /** Manages auto-unscription on component destroy */
    private destroyRef = inject(DestroyRef);

    /**
     * Holds dealer autocomplete options
     */
    protected dealerOptions = signal<UiDealersAutocomplete[]>([]);

    /**
     * Holds host autocomplete options
     */
    protected hostOptions = signal<UiHostsAutocomplete[] | null>(null);

    /**
     * Holds advertiser autocomplete options
     */
    protected advertiserOptions = signal<UiAdvertisersAutocomplete[] | null>(null);

    /**
     * Two-way binding for the selected dealer
     */
    public selectedDealer = model<{ label: string; value: string }[]>([]);

    /**
     * Two-way binding for the selected host
     */
    public selectedHost = model<{ label: string; value: string }[]>([]);

    /**
     * Two-way binding for the selected advertiser
     */
    public selectedAdvertiser = model<{ label: string; value: string }[]>([]);

    /**
     * Query parameters for dealers including filtering
     */
    private dealerQueryParams = signal<BaseQueryParams>({
        limit: 20,
    });

    /**
     * Holds the advertiser client type id used to filter dealer's advertisers
     */
    private advertiserClientTypeId = signal<string | null>(null);

    /** Holds the dealer client type id used to filter dealers */
    private dealerClientTypeId = signal<string | null>(null);

    /** Holds the debounced search key for searching, dealers, hosts, and advertisers */
    private searchKey = signal<string>('');

    /** Holds the current focus field to determine which search user to trigger */
    private focusedField = signal<string>('');

    /**
     * Flag indicating that loading/searching dealers is pending
     */
    protected isLoadingDealers = signal<boolean>(false);

    /**
     * Flag indicating that loading/searching dealer hosts is pending
     */
    protected isLoadingDealerHosts = signal<boolean>(false);

    /**
     * Flag indicating that loading/searching advertisers is pending
     */
    protected isLoadingDealerAdvertisers = signal<boolean>(false);

    /**
     * Reactive flag whether host options are available
     */
    protected hostOptionsAvailable = computed(
        () => this.hostOptions() && this.hostOptions()!.length > 0,
    );

    /**
     * Reactive flag whether advertiser options are available
     */
    protected advertiserOptionsAvailable = computed(
        () => this.advertiserOptions() && this.advertiserOptions()!.length > 0,
    );

    /**
     * Dealer autocomplete config
     */
    protected dealerConfig = {
        placeholder: 'Select Dealer Business Name',
        searchable: true,
        multiple: false,
    };

    /**
     * Host autocomplete config
     */
    protected hostConfig = {
        ...this.dealerConfig,
        placeholder: 'Select Host',
    };

    /**
     * Advertiser autocomplete config
     */
    protected advertiserConfig = {
        ...this.dealerConfig,
        placeholder: 'Select Advertiser',
    };

    constructor() {
        /**
         * Load hosts under the selected dealer
         */
        effect(() => {
            const queryParam = this.dealerQueryParams();
            const selectedDealerId = this.selectedDealer()[0]?.value;

            untracked(() => {
                if (queryParam.filters?.['dealerOwnerId'] || selectedDealerId) {
                    this.loadDealerHosts(queryParam);
                }
            });
        });

        /**
         * Load advertisers under the selected dealer
         */
        effect(() => {
            const selectedDealerId = this.selectedDealer()[0]?.value;
            const advertiserClientTypeId = this.advertiserClientTypeId();

            // construct filtering params
            const filters: Record<string, FilterOperator> = {
                clientInformationId: { _eq: selectedDealerId },
                clientTypeId: { _eq: advertiserClientTypeId },
            };

            untracked(() => {
                if (selectedDealerId && advertiserClientTypeId) {
                    this.loadDealerAdvertisers({ filters });
                }
            });
        });

        /**
         * Search handler for searching, dealers, hosts, or advertisers.
         * Deligates the search operation to the appropriate service based on the focused field
         */
        searchHandler(
            this.searchKey,
            (searchKey) => {
                const field = this.focusedField();
                switch (field) {
                    /** Dealer search */
                    case ClientTypeCode.DEALER:
                        return this.dealerService
                            .getAll({
                                filters: {
                                    clientTypeId: { _eq: this.dealerClientTypeId() },
                                    businessName: { _ilike: `${searchKey}` },
                                },
                            })
                            .pipe(withLoading(this.isLoadingDealers));
                    /** Host search */
                    case ClientTypeCode.HOST:
                        return this.dealerService
                            .getDealerHosts({
                                filters: {
                                    dealerOwnerId: { _eq: this.selectedDealer()[0]?.value },
                                    hostOwnerId: { _ilike: `${searchKey}` },
                                },
                            })
                            .pipe(withLoading(this.isLoadingDealerHosts));
                    /** Advertiser search */
                    case ClientTypeCode.ADVERTISER:
                        return this.dealerService
                            .searchAdvertisers(searchKey)
                            .pipe(withLoading(this.isLoadingDealerAdvertisers));
                    default:
                        return EMPTY;
                }
            },
            this.destroyRef,
            {
                next: (res) => {
                    const field = this.focusedField();
                    if (field === ClientTypeCode.DEALER) {
                        this.mapDataToUi(res.data);
                    } else if (field === ClientTypeCode.HOST) {
                        this.mapDataToUi(null, res.data);
                    } else if (field === ClientTypeCode.ADVERTISER) {
                        this.mapDataToUi(null, null, res.data);
                    }
                },
                onEmpty: () => {
                    const field = this.focusedField();
                    if (field === ClientTypeCode.DEALER) {
                        this.loadInitialDealers(this.dealerClientTypeId()!);
                    } else if (field === ClientTypeCode.HOST && this.selectedDealer()[0]?.value) {
                        this.loadDealerHosts(this.dealerQueryParams());
                    } else if (
                        field === ClientTypeCode.ADVERTISER &&
                        this.selectedDealer()[0]?.value
                    ) {
                        this.loadDealerAdvertisers({
                            filters: {
                                clientInformationId: { _eq: this.selectedDealer()[0]?.value },
                                clientTypeId: { _eq: this.advertiserClientTypeId() },
                            },
                        });
                    }
                },
            },
        );
    }

    ngOnInit() {
        this.loadDealerClientType();
        this.loadAdvertiserClientType();
    }

    /**
     * Updates the focused field allowing us to determine to which search the operation is for
     *
     * @param str - The focused field
     */
    protected onFocus(str: 'dealer' | 'host' | 'advertiser'): void {
        this.focusedField.set(str);
    }

    /**
     * Updates dealer filter params based on the selected option and sets the selected deaeler
     * Removes `dealerOwnerId` filter if no option is selected, otherwise filters by the selected dealer.
     *
     * @param option - The selected dealer from the autocomplete.
     */
    protected onDealerSelect(option: UiDealersAutocomplete): void {
        if (!option) {
            const { filters, ...rest } = this.dealerQueryParams();
            delete filters?.['dealerOwnerId'];
            this.dealerQueryParams.set({ filters, ...rest });
            this.hostOptions.set(null);
            this.advertiserOptions.set(null);
            return;
        }

        this.selectedDealer.set([{ label: option.label, value: option.value }]);
        this.dealerQueryParams.update((prev) => {
            return {
                ...prev,
                filters: { dealerOwnerId: { _eq: option.value } },
            };
        });
    }

    /**
     * Updates host filter params based on the selected option and sets the selected host
     * Removes `hostOwnerId` filter if no option is selected, otherwise filters by the selected host.
     *
     * @param option - The selected host from the autocomplete.
     */
    protected onHostSelect(option: UiHostsAutocomplete): void {
        if (!option) {
            const { filters, ...rest } = this.dealerQueryParams();
            delete filters?.['hostOwnerId'];
            this.dealerQueryParams.set({ filters, ...rest });
            this.advertiserOptions.set(null);
            return;
        }

        this.selectedHost.set([{ label: option.label, value: option.value }]);
        this.dealerQueryParams.update((prev) => {
            return {
                ...prev,
                filters: { hostOwnerId: { _eq: option.value } },
            };
        });
    }

    /**
     * Updates advertiser filter params based on the selected option and sets the selected advertiser
     * Removes `advertiserOwnerId` filter if no option is selected, otherwise filters by the selected advertiser.
     *
     * @param option - The selected advertiser from the autocomplete.
     */
    protected onAdvertiserSelect(option: UiAdvertisersAutocomplete): void {
        if (!option) {
            const { filters, ...rest } = this.dealerQueryParams();
            delete filters?.['advertiserOwnerId'];
            this.dealerQueryParams.set({ filters, ...rest });
            return;
        }

        this.selectedAdvertiser.set([{ label: option.label, value: option.value }]);
        this.dealerQueryParams.update((prev) => {
            return {
                ...prev,
                filters: { advertiserOwnerId: { _eq: option.value } },
            };
        });
    }

    /**
     * Search handler for the search key signal
     *
     * @param searchKey - Debounced search key
     */
    protected onSearch(searchKey: string): void {
        this.searchKey.set(searchKey);
    }

    /**
     * Maps API response to fit UI requirements
     *
     * @param dealers - The list of dealers response to map. Nullable
     * @param hosts - The list of hosts response to map. Nullable
     * @param advertisers - The list of advertisers response to map. Nullable
     */
    private mapDataToUi(
        dealers?: ApiDealerInformation[] | null,
        hosts?: ApiDealerHost[] | null,
        advertisers?: ApiDealerAdvertiser[] | null,
    ): void {
        if (dealers) {
            const mappedDealers: UiDealersAutocomplete[] = dealers.map((d) => ({
                ...d.clientInformation,
                label: d.clientInformation.businessName,
                value: d.clientInformation.ownerId,
            }));
            this.dealerOptions.set(mappedDealers);
        }

        if (hosts) {
            const mappedHosts: UiHostsAutocomplete[] = hosts.map((h) => {
                return {
                    ...h,
                    label: h.hostOwnerId,
                    value: h.hostOwnerId,
                };
            });
            this.hostOptions.set(mappedHosts);
        }

        if (advertisers) {
            const mappedAdvertisers: UiAdvertisersAutocomplete[] = advertisers.map((a) => {
                return {
                    ...a,
                    label: a.advertiserOwnerId,
                    value: a.advertiserOwnerId,
                };
            });
            this.advertiserOptions.set(mappedAdvertisers);
        }
    }

    /** ================================= API Calls ===================================== */

    /**
     * Resolves the "Dealer" client type ID and loads initial dealers
     */
    private loadDealerClientType(): void {
        this.dealerService
            .getClientType(ClientTypeCode.DEALER)
            .pipe(
                catchError((error: unknown) => {
                    Logger.error(
                        'DealerSelectionBaseComponent.loadDealerClientTypeId',
                        'error loading client types',
                        error,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                if (!response.data.length) return;
                this.loadInitialDealers(response.data[0].clientTypeId);
                this.dealerClientTypeId.set(response.data[0].clientTypeId);
            });
    }

    /**
     * Resolves the "Advertiser" client type ID
     */
    private loadAdvertiserClientType(): void {
        this.dealerService
            .getClientType(ClientTypeCode.ADVERTISER)
            .pipe(
                catchError((error: unknown) => {
                    Logger.error(
                        'DealerSelectionBaseComponent.loadAdvertiserClientTypeId',
                        'error loading client types',
                        error,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                if (!response.data.length) return;
                this.advertiserClientTypeId.set(response.data[0].clientTypeId);
            });
    }

    /**
     * Loads the initial list of dealers based on the client type ID
     * @param clientTypeId - The ID used to filter for dealer-type users
     */
    private loadInitialDealers(clientTypeId: string): void {
        if (!clientTypeId) return;

        // Client Type Id for user of type "dealer"
        const filters = {
            clientTypeId: { _eq: clientTypeId },
        };

        this.dealerService
            .getAll({ filters })
            .pipe(
                withLoading(this.isLoadingDealers),
                catchError((error: unknown) => {
                    Logger.error(
                        'DealerSelectionBaseComponent.loadInitialDealers',
                        'error loading dealers',
                        error,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                this.mapDataToUi(response.data);
            });
    }

    /**
     * Loads hosts associated with the selected dealer
     * @param queryParams - Filtering and pagination parameters
     */
    private loadDealerHosts(queryParams: BaseQueryParams): void {
        this.dealerService
            .getDealerHosts(queryParams)
            .pipe(
                withLoading(this.isLoadingDealerHosts),
                catchError((error: unknown) => {
                    Logger.error(
                        'DealerSelectionBaseComponent.loadDealerHosts',
                        'error loading dealer hosts',
                        error,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                this.mapDataToUi(null, response.data);
            });
    }

    /**
     * Loads advertisers associated with the selected dealer
     * @param queryParams - Filtering and pagination parameters
     */
    private loadDealerAdvertisers(queryParams: BaseQueryParams): void {
        this.dealerService
            .getDealerAdvertisers(queryParams)
            .pipe(
                withLoading(this.isLoadingDealerAdvertisers),
                catchError((error: unknown) => {
                    Logger.error(
                        'DealerSelectionBaseComponent.loadDealerAdvertisers',
                        'error loading dealer advertisers',
                        error,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                console.log('advertisers', response);
            });
    }
}
